import React, { useState, useRef } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Camera, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '../utils/imageCompress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AvatarUpload = () => {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const avatarSrc = preview
    || (user?.custom_avatar ? `${BACKEND_URL}${user.custom_avatar}` : null)
    || user?.picture
    || null;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error(language === 'fr' ? 'Format non supporté (JPG, PNG, WebP)' : 'Unsupported format (JPG, PNG, WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'fr' ? 'Image trop lourde (max 5 Mo)' : 'Image too large (max 5 MB)');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      // Compress avatar to 512px max (enough for 20x20 display, saves bandwidth + storage)
      const compressed = await compressImage(file, 512, 0.9).catch(() => file);
      const formData = new FormData();
      formData.append('file', compressed, 'avatar.jpg');
      const res = await api.post('/users/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser(res.data.user);
      setPreview(null);
      toast.success(language === 'fr' ? 'Photo mise à jour !' : 'Photo updated!');
    } catch (err) {
      setPreview(null);
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!user?.custom_avatar) return;
    setUploading(true);
    try {
      const res = await api.delete('/users/avatar');
      updateUser(res.data.user);
      setPreview(null);
      toast.success(language === 'fr' ? 'Photo supprimée' : 'Photo removed');
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-5" data-testid="avatar-upload-section">
      <div className="relative group">
        <Avatar className="h-20 w-20 ring-2 ring-primary/30">
          <AvatarImage src={avatarSrc} alt={user?.name} />
          <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
            {user?.name?.charAt(0)?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="absolute bottom-0 right-0 w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          data-testid="avatar-upload-btn"
        >
          <Camera className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 space-y-1.5">
        <p className="font-heading text-lg uppercase tracking-tight text-foreground">{user?.name}</p>
        <p className="text-xs text-muted-foreground">{user?.email}</p>
        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="avatar-change-btn"
          >
            <Camera className="w-3.5 h-3.5 mr-1.5" />
            {language === 'fr' ? 'Changer la photo' : 'Change photo'}
          </Button>
          {user?.custom_avatar && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={uploading}
              className="text-muted-foreground hover:text-destructive"
              data-testid="avatar-remove-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              {language === 'fr' ? 'Supprimer' : 'Remove'}
            </Button>
          )}
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileSelect}
        data-testid="avatar-file-input"
      />
    </div>
  );
};

export default AvatarUpload;
